/**
 * Call an HR API method with ensureHrToken + one retry after clearing a bad token.
 */
export async function fetchWithHrToken(ensureHrToken, requestFn) {
  let token = await ensureHrToken();
  if (!token) return { error: 'no_token' };

  try {
    return { data: await requestFn(token), token };
  } catch (err) {
    if (err.code === 401 || /invalid or expired token/i.test(err.message || '')) {
      localStorage.removeItem('ea_hr_token');
      token = await ensureHrToken();
      if (!token) return { error: err.message };
      try {
        return { data: await requestFn(token), token };
      } catch (retryErr) {
        return { error: retryErr.message };
      }
    }
    return {
      error: err.message,
      code: err.code ?? err.httpStatus,
      result: err.result,
      httpStatus: err.httpStatus,
    };
  }
}

export const HR_SESSION_ERROR =
  'Could not start an officer API session. Sign out and sign in again (shared HR/Admission/Exam login).';

/** Pad number to 2 digits. */
function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Convert API UTC datetime ("YYYY-MM-DD HH:MM:SS") to local datetime-local value.
 */
export function utcToLocalInput(utcStr) {
  if (!utcStr) return '';
  const trimmed = String(utcStr).trim();
  if (!trimmed || trimmed === 'false') return '';

  let d;
  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    d = new Date(trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T'));
  } else {
    const normalized = trimmed.replace(' ', 'T');
    d = new Date(`${normalized}${normalized.length === 16 ? ':00' : ''}Z`);
  }
  if (Number.isNaN(d.getTime())) return '';

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
}

/**
 * Convert local datetime-local value to API UTC "YYYY-MM-DD HH:MM:SS".
 */
export function localInputToUtc(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(
    d.getUTCHours()
  )}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}`;
}

export function todayIsoLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function daysAgoIsoLocal(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
